import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { FiSearch, FiX } from "react-icons/fi";
import { useCatalogStore } from "@/features/catalog/stores/catalogStore";
import { SearchScopeSelect } from "@/features/search/components/SearchScopeSelect";
import {
  type SearchScope,
  serializeSearchScope,
} from "@/features/search/searchScope";
import { cn } from "@/shared/lib";
import { Input } from "@/shared/ui/form";

const MAX_SEARCH_QUERY_LENGTH = 100;

export interface SearchDestination {
  to: "/search";
  search: { q: string; scope?: "op" };
}

export type SearchNavigator = (destination: SearchDestination) => unknown;

// biome-ignore lint/style/useComponentExportOnlyModules: pure helper is exported for direct testing
export function buildSearchDestination(
  value: string,
  scope: SearchScope = "all",
): SearchDestination | null {
  const query = value.trim().slice(0, MAX_SEARCH_QUERY_LENGTH);
  if (!query) return null;

  const serializedScope = serializeSearchScope(scope);
  return {
    to: "/search",
    search: {
      q: query,
      ...(serializedScope ? { scope: serializedScope } : {}),
    },
  };
}

// biome-ignore lint/style/useComponentExportOnlyModules: pure helper is exported for direct testing
export function runFullPostSearch(
  value: string,
  navigate: SearchNavigator,
  scope: SearchScope = "all",
): boolean {
  const destination = buildSearchDestination(value, scope);
  if (!destination) return false;

  void navigate(destination);
  return true;
}

interface SearchBarProps {
  className?: string;
}

export const SearchBar: React.FunctionComponent<SearchBarProps> = (
  props: SearchBarProps,
) => {
  const { className } = props;
  const { searchQuery, searchScope, setSearchQuery, setSearchScope } =
    useCatalogStore();
  const navigate = useNavigate();

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchQuery(e.target.value);
    },
    [setSearchQuery],
  );

  const handleClear = useCallback(() => {
    setSearchQuery("");
  }, [setSearchQuery]);

  const handleSubmit = useCallback(
    (event: React.SubmitEvent<HTMLFormElement>): void => {
      event.preventDefault();
      runFullPostSearch(searchQuery, navigate, searchScope);
    },
    [navigate, searchQuery, searchScope],
  );

  return (
    <search aria-label="投稿検索" className={cn("w-full md:w-auto", className)}>
      <form
        onSubmit={handleSubmit}
        className="catalog-search-form flex w-full items-center gap-2"
      >
        <SearchScopeSelect
          ariaLabel="投稿検索の検索モード"
          scope={searchScope}
          onChange={setSearchScope}
        />
        <div
          className="catalog-search-field relative flex min-w-0 flex-1 items-center"
          data-has-query={searchQuery ? "true" : "false"}
        >
          <Input
            type="text"
            value={searchQuery}
            onChange={handleChange}
            placeholder="投稿検索"
            aria-label="投稿検索"
            maxLength={MAX_SEARCH_QUERY_LENGTH}
            className={`catalog-search-input min-h-11 min-w-0 flex-1 ${
              searchQuery ? "pr-24" : "pr-12"
            }`}
          />
          <fieldset
            aria-label="検索操作"
            className="absolute inset-y-0 right-0 m-0 flex min-w-0 items-center border-0 p-0"
          >
            {searchQuery && (
              <button
                type="button"
                onClick={handleClear}
                className="catalog-search-action flex size-11 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                aria-label="検索クリア"
              >
                <FiX size={18} aria-hidden="true" />
              </button>
            )}
            <button
              type="submit"
              disabled={!searchQuery.trim()}
              className={cn(
                "catalog-search-action flex size-11 items-center justify-center text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed disabled:opacity-50",
                searchQuery && "catalog-search-action-divider",
              )}
              aria-label="検索"
            >
              <FiSearch size={18} aria-hidden="true" />
            </button>
          </fieldset>
        </div>
      </form>
    </search>
  );
};
